import { Button, Tile, Tag } from "@carbon/react";
import { ArrowRight, Information, WarningAlt, Renew, ChartLine, Currency, TaskComplete, ShoppingCart } from "@carbon/icons-react";
import { useGetList } from "ra-core";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardStepper } from "./DashboardStepper";
import { Link } from "react-router";
import { useMemo, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { startOfMonth, startOfQuarter, startOfYear, subMonths, format } from "date-fns";

import type { Company, Contact, Deal, Sale, Task } from "../types";
import "./BusinessDataHub.css";

type Period = "month" | "quarter" | "year" | "all";
type MetricCardProps = {
  label: string;
  value: string;
  helper: string;
  status?: "connected" | "pending" | "neutral";
  icon: ComponentType<any>;
};

const money = (value: number, currency = "USD") =>
  new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    notation: Math.abs(value) >= 1_000_000 ? "compact" : "standard",
  }).format(value || 0);

const dateForPeriod = (period: Period) => {
  const now = new Date();
  if (period === "month") return startOfMonth(now);
  if (period === "quarter") return startOfQuarter(now);
  if (period === "year") return startOfYear(now);
  return new Date(0);
};

const stageName: Record<string, string> = {
  opportunity: "新建商机",
  "proposal-sent": "方案已发送",
  "in-negociation": "商务谈判",
  delayed: "推进延迟",
  won: "已赢单",
  lost: "已输单",
};

const MetricCard = ({ label, value, helper, status = "connected", icon: Icon }: MetricCardProps) => (
  <Tile className="orbitas-hub-kpi">
    <div className="orbitas-hub-kpi__top">
      <span className="orbitas-hub-kpi__label">{label}</span>
      <Icon size={20} aria-hidden="true" />
    </div>
    <div className="orbitas-hub-kpi__value">{value}</div>
    <div className="orbitas-hub-kpi__footer">
      <span className={`orbitas-hub-status orbitas-hub-status--${status}`} aria-hidden="true" />
      <span>{helper}</span>
    </div>
  </Tile>
);

const SectionHeading = ({ eyebrow, title, description, action }: {
  eyebrow: string; title: string; description: string; action?: ReactNode;
}) => (
  <div className="orbitas-hub-section-heading">
    <div>
      <div className="orbitas-hub-eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
    {action}
  </div>
);

const NotConnected = ({ label }: { label: string }) => (
  <div className="orbitas-hub-not-connected">
    <Information size={16} aria-hidden="true" />
    <span>{label}：尚未接入数据源</span>
  </div>
);

export const Dashboard = () => {
  const [period, setPeriod] = useState<Period>("month");
  const [country, setCountry] = useState("all");
  const [sector, setSector] = useState("all");
  const [salesId, setSalesId] = useState("all");

  const dealsQuery = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "DESC" },
  });
  const companiesQuery = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "DESC" },
  });
  const contactsQuery = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "first_seen", order: "DESC" },
  });
  const notesQuery = useGetList<{ id: string | number; contact_id?: string | number }>("contact_notes", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "date", order: "DESC" },
  });
  const tasksQuery = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "due_date", order: "ASC" },
  });
  const salesQuery = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "first_name", order: "ASC" },
  });

  const isPending = dealsQuery.isPending || companiesQuery.isPending ||
    contactsQuery.isPending || tasksQuery.isPending || salesQuery.isPending;
  const hasError = dealsQuery.isError || companiesQuery.isError ||
    contactsQuery.isError || tasksQuery.isError || salesQuery.isError;

  const deals = dealsQuery.data ?? [];
  const companies = companiesQuery.data ?? [];
  const contacts = contactsQuery.data ?? [];
  const tasks = tasksQuery.data ?? [];
  const notes = notesQuery.data ?? [];
  const onboardingStep = contacts.length === 0 ? 1 : notes.length === 0 ? 2 : 3;
  const onboardingContactId = contacts[0]?.id;
  const sales = salesQuery.data ?? [];

  const countries = useMemo(() => [...new Set(companies.map((company) => company.country).filter(Boolean))].sort(), [companies]);
  const sectors = useMemo(() => [...new Set(companies.map((company) => company.sector).filter(Boolean))].sort(), [companies]);

  const visibleCompanies = useMemo(() => companies.filter((company) =>
    (country === "all" || company.country === country) &&
    (sector === "all" || company.sector === sector) &&
    (salesId === "all" || String(company.sales_id ?? "") === salesId)
  ), [companies, country, sector, salesId]);

  const companyIds = useMemo(() => new Set(visibleCompanies.map((company) => String(company.id))), [visibleCompanies]);
  const periodStart = dateForPeriod(period);
  const visibleDeals = useMemo(() => deals.filter((deal) => {
    const createdAt = deal.created_at ? new Date(deal.created_at) : null;
    const matchesPeriod = period === "all" || (!!createdAt && createdAt >= periodStart);
    const companyMatches = country === "all" && sector === "all" && salesId === "all"
      ? true : companyIds.has(String(deal.company_id));
    const teamMatches = salesId === "all" || String(deal.sales_id ?? "") === salesId;
    return matchesPeriod && companyMatches && teamMatches;
  }), [deals, period, periodStart, companyIds, country, sector, salesId]);

  const visibleContacts = useMemo(() => contacts.filter((contact) => {
    const companyMatches = country === "all" && sector === "all" && salesId === "all"
      ? true : companyIds.has(String(contact.company_id));
    const teamMatches = salesId === "all" || String(contact.sales_id ?? "") === salesId;
    const created = contact.first_seen ? new Date(contact.first_seen) : null;
    return companyMatches && teamMatches && (period === "all" || (!!created && created >= periodStart));
  }), [contacts, companyIds, country, sector, salesId, period, periodStart]);

  const filteredTasks = useMemo(() => tasks.filter((task) =>
    salesId === "all" || String(task.sales_id ?? "") === salesId
  ), [tasks, salesId]);

  const metrics = useMemo(() => {
    const activeDeals = visibleDeals.filter((deal) => !["won", "lost"].includes(deal.stage));
    const wonDeals = visibleDeals.filter((deal) => deal.stage === "won");
    const closedDeals = visibleDeals.filter((deal) => ["won", "lost"].includes(deal.stage));
    const openTasks = filteredTasks.filter((task) => !task.done_date);
    const newDeals = visibleDeals.filter((deal) => {
      const created = deal.created_at ? new Date(deal.created_at) : null;
      return !!created && created >= periodStart;
    });
    return {
      pipeline: activeDeals.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0),
      newAmount: newDeals.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0),
      winRate: closedDeals.length ? Math.round((wonDeals.length / closedDeals.length) * 100) : null,
      openTasks: openTasks.length,
      overdueTasks: openTasks.filter((task) => task.due_date && new Date(task.due_date) < new Date()).length,
      wonAmount: wonDeals.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0),
      closedCount: closedDeals.length,
    };
  }, [visibleDeals, filteredTasks, periodStart]);

  // The trend always spans six calendar months. Apply the account and owner
  // filters, but do not apply the KPI period selector; otherwise choosing
  // "this month" would erase the previous five months from the chart.
  const monthlyTrend = useMemo(() => {
    const now = new Date();
    const trendDeals = deals.filter((deal) => {
      const companyMatches =
        country === "all" && sector === "all" && salesId === "all"
          ? true
          : companyIds.has(String(deal.company_id));
      const teamMatches =
        salesId === "all" || String(deal.sales_id ?? "") === salesId;
      return companyMatches && teamMatches;
    });

    return Array.from({ length: 6 }, (_, index) => {
      const month = subMonths(startOfMonth(now), 5 - index);
      const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
      const items = trendDeals.filter((deal) => {
        if (!deal.created_at) return false;
        const created = new Date(deal.created_at);
        return created >= month && created < nextMonth;
      });
      return {
        label: format(month, "M月"),
        amount: items.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0),
        count: items.length,
      };
    });
  }, [deals, country, sector, salesId, companyIds]);
  const maxTrend = Math.max(...monthlyTrend.map((item) => item.amount), 1);

  const funnel = useMemo(() => {
    const stages = ["opportunity", "proposal-sent", "in-negociation", "delayed", "won", "lost"];
    return stages.map((stage) => {
      const items = visibleDeals.filter((deal) => deal.stage === stage);
      return {
        stage,
        label: stageName[stage] ?? stage,
        count: items.length,
        amount: items.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0),
      };
    }).filter((stage) => stage.count > 0 || ["opportunity", "proposal-sent", "in-negociation", "won", "lost"].includes(stage.stage));
  }, [visibleDeals]);
  const maxFunnel = Math.max(...funnel.map((item) => item.amount), 1);

  const risks = useMemo(() => {
    const overdue = filteredTasks.filter((task) => !task.done_date && task.due_date && new Date(task.due_date) < new Date())
      .slice(0, 4).map((task) => ({ id: String(task.id), title: task.text || "待办任务", detail: `截止日期：${task.due_date ? format(new Date(task.due_date), "yyyy-MM-dd") : "未设置"}`, severity: "high" as const, href: "/tasks" }));
    const stalled = visibleDeals.filter((deal) => !["won", "lost"].includes(deal.stage) && deal.updated_at && new Date(deal.updated_at) < subMonths(new Date(), 1))
      .slice(0, 4).map((deal) => ({ id: String(deal.id), title: deal.name, detail: `商机超过 1 个月未更新 · ${stageName[deal.stage] ?? deal.stage}`, severity: "medium" as const, href: "/deals" }));
    return [...overdue, ...stalled].slice(0, 6);
  }, [filteredTasks, visibleDeals]);

  if (isPending) {
    return <main className="orbitas-hub orbitas-hub--loading" aria-label="业务数据中枢加载中">
      <div className="orbitas-hub-loading-line" /><div className="orbitas-hub-loading-line" /><div className="orbitas-hub-loading-grid" />
    </main>;
  }

  if (hasError) {
    return <main className="orbitas-hub">
      <div className="orbitas-hub-page-header"><div><div className="orbitas-hub-eyebrow">ORBITAS / BUSINESS INTELLIGENCE</div><h1>业务数据中枢</h1><p>统一查看销售、客户与业务执行情况。</p></div></div>
      <div className="orbitas-hub-error" role="alert"><WarningAlt size={20} /><div><strong>数据暂时无法加载</strong><p>请检查 CRM 数据连接后重试。未加载成功的数据不会以示例数字代替。</p></div><Button kind="tertiary" renderIcon={Renew} onClick={() => { void dealsQuery.refetch(); void companiesQuery.refetch(); void contactsQuery.refetch(); void tasksQuery.refetch(); void salesQuery.refetch(); }}>重新加载</Button></div>
    </main>;
  }

  if (onboardingStep < 3) {
    return (
      <main className="orbitas-hub orbitas-hub--onboarding">
        <DashboardStepper step={onboardingStep} contactId={onboardingContactId} />
      </main>
    );
  }

  return (
    <main className="orbitas-hub">
      <div className="orbitas-hub-page-header">
        <div>
          <div className="orbitas-hub-eyebrow">ORBITAS / BUSINESS INTELLIGENCE</div>
          <h1>业务数据中枢</h1>
          <p>统一经营视图 · 销售、客户、订单与服务数据</p>
        </div>
        <div className="orbitas-hub-header-actions">
          <Tag type="blue">CRM 当前数据</Tag>
          <Button kind="tertiary" size="sm" renderIcon={Renew} onClick={() => { void dealsQuery.refetch(); void companiesQuery.refetch(); void contactsQuery.refetch(); void tasksQuery.refetch(); void salesQuery.refetch(); }}>刷新数据</Button>
        </div>
      </div>

      <section className="orbitas-hub-filter-bar" aria-label="全局筛选器">
        <div className="orbitas-hub-filter-title"><span>全局筛选</span><span className="orbitas-hub-filter-caption">影响已接入的 CRM 指标</span></div>
        <label className="orbitas-hub-filter"><span>统计周期</span><select className="cds--select-input" value={period} onChange={(event) => setPeriod(event.target.value as Period)}><option value="month">本月</option><option value="quarter">本季度</option><option value="year">本年度</option><option value="all">全部时间</option></select></label>
        <label className="orbitas-hub-filter"><span>区域 / 国家</span><select className="cds--select-input" value={country} onChange={(event) => setCountry(event.target.value)}><option value="all">全部区域</option>{countries.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        <label className="orbitas-hub-filter"><span>客户行业</span><select className="cds--select-input" value={sector} onChange={(event) => setSector(event.target.value)}><option value="all">全部行业</option>{sectors.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        <label className="orbitas-hub-filter"><span>销售负责人</span><select className="cds--select-input" value={salesId} onChange={(event) => setSalesId(event.target.value)}><option value="all">全部团队 / 负责人</option>{sales.map((person) => <option key={person.id} value={String(person.id)}>{[person.first_name, person.last_name].filter(Boolean).join(" ") || person.email}</option>)}</select></label>
        <Button kind="ghost" size="sm" onClick={() => { setPeriod("month"); setCountry("all"); setSector("all"); setSalesId("all"); }}>重置</Button>
      </section>

      <section className="orbitas-hub-kpis" aria-label="核心经营指标">
        <MetricCard label="本期收入" value="—" helper="需接入 SAP 财务 / 开票数据" status="pending" icon={Currency} />
        <MetricCard label="目标达成率" value="—" helper="需配置销售目标与财年口径" status="pending" icon={ChartLine} />
        <MetricCard label="未结商机金额" value={money(metrics.pipeline)} helper={`${visibleDeals.filter((deal) => !["won", "lost"].includes(deal.stage)).length} 个进行中商机 · CRM`} icon={Currency} />
        <MetricCard label="本期新增商机金额" value={money(metrics.newAmount)} helper="按商机创建日期统计" icon={ChartLine} />
        <MetricCard label="赢单率" value={metrics.winRate === null ? "—" : `${metrics.winRate}%`} helper={metrics.closedCount ? "已关闭商机中赢单占比" : "暂无已关闭商机数据"} icon={ChartLine} />
        <MetricCard label="未完成任务" value={String(metrics.openTasks)} helper={metrics.overdueTasks ? `${metrics.overdueTasks} 项已逾期` : "当前未完成任务"} icon={TaskComplete} />
      </section>

      <section className="orbitas-hub-section">
        <SectionHeading eyebrow="01 / EXECUTIVE OVERVIEW" title="经营总览" description="查看商机变化、当前销售漏斗及需要优先处理的风险。" />
        <div className="orbitas-hub-grid orbitas-hub-grid--overview">
          <Tile className="orbitas-hub-panel">
            <div className="orbitas-hub-panel-heading"><div><h3>商机金额趋势</h3><p>近 6 个月 · 按创建时间汇总，不代表确认收入</p></div><Tag type="cool-gray">CRM</Tag></div>
            {monthlyTrend.every((item) => item.count === 0) ? <div className="orbitas-hub-empty">所选条件下暂无商机数据</div> : <div className="orbitas-hub-trend">
              <div className="orbitas-hub-trend-chart" role="img" aria-label="近六个月商机金额柱状图">
                {monthlyTrend.map((item) => <div className="orbitas-hub-trend-column" key={item.label} title={`${item.label}：${money(item.amount)}，${item.count} 个商机`}><span className="orbitas-hub-trend-value">{item.amount ? money(item.amount) : "—"}</span><div className="orbitas-hub-trend-bar-wrap"><div className="orbitas-hub-trend-bar" style={{ height: `${Math.max(item.amount / maxTrend * 100, item.count ? 4 : 0)}%` }} /></div><span className="orbitas-hub-trend-label">{item.label}</span></div>)}
              </div>
            </div>}
          </Tile>
          <Tile className="orbitas-hub-panel">
            <div className="orbitas-hub-panel-heading"><div><h3>销售漏斗</h3><p>按当前商机阶段统计</p></div><Link className="orbitas-hub-text-link" to="/deals">查看商机 <ArrowRight size={14} /></Link></div>
            <div className="orbitas-hub-funnel">{funnel.map((item) => <div className="orbitas-hub-funnel-row" key={item.stage}><div className="orbitas-hub-funnel-meta"><span>{item.label}</span><strong>{item.count} 个</strong></div><div className="orbitas-hub-funnel-track"><div className={`orbitas-hub-funnel-fill orbitas-hub-funnel-fill--${item.stage === "won" ? "won" : item.stage === "lost" ? "lost" : "open"}`} style={{ width: `${item.amount ? Math.max(3, item.amount / maxFunnel * 100) : item.count ? 3 : 0}%` }} /></div><span className="orbitas-hub-funnel-amount">{money(item.amount)}</span></div>)}</div>
          </Tile>
        </div>
      </section>

      <section className="orbitas-hub-section">
        <SectionHeading eyebrow="02 / SALES PERFORMANCE" title="销售业绩与预测" description="以现有 CRM 商机数据呈现阶段分布；预测、配额与真实成交周期需补充数据模型。" action={<Button kind="tertiary" size="sm" renderIcon={ArrowRight} href="/deals">打开商机列表</Button>} />
        <div className="orbitas-hub-grid orbitas-hub-grid--three">
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">已赢单商机金额</div><div className="orbitas-hub-stat-value">{money(metrics.wonAmount)}</div><p>商机金额，不等同财务确认收入</p></Tile>
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">本期新增商机</div><div className="orbitas-hub-stat-value">{visibleDeals.filter((deal) => deal.created_at && new Date(deal.created_at) >= periodStart).length}</div><p>按商机创建日期统计</p></Tile>
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">客户 / 联系人</div><div className="orbitas-hub-stat-value">{visibleCompanies.length} <span>/</span> {visibleContacts.length}</div><p>当前筛选条件下的 CRM 记录</p></Tile>
        </div>
        <div className="orbitas-hub-connector-row"><NotConnected label="销售目标与预测" /><NotConnected label="平均成交周期与阶段历史" /><NotConnected label="销售排名所需配额口径" /></div>
      </section>

      <section className="orbitas-hub-section">
        <SectionHeading eyebrow="03 / ORDER TO CASH" title="订单、交付与回款" description="为商机 → 报价 → 订单 → 开票 → 回款的端到端链路预留统一视图。" />
        <Tile className="orbitas-hub-panel">
          <div className="orbitas-hub-order-flow">
            {["商机", "报价", "订单", "开票", "回款"].map((step, index) => <div className="orbitas-hub-order-step" key={step}><div className={`orbitas-hub-order-step__number ${index === 0 ? "is-active" : ""}`}>{String(index + 1).padStart(2, "0")}</div><strong>{step}</strong><span>{index === 0 ? `${visibleDeals.length} 条 CRM 记录` : "尚未接入"}</span></div>)}
          </div>
          <div className="orbitas-hub-connector-row"><NotConnected label="报价与报价明细" /><NotConnected label="SAP 订单与交付状态" /><NotConnected label="发票、应收账龄与实际回款" /><NotConnected label="可供库存" /></div>
        </Tile>
      </section>

      <section className="orbitas-hub-section">
        <SectionHeading eyebrow="04 / CUSTOMER SERVICE" title="客户服务与交付质量" description="当前展示 CRM 任务执行情况；客服工单和 SLA 指标需要服务系统接入。" />
        <div className="orbitas-hub-grid orbitas-hub-grid--three">
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">未完成任务</div><div className="orbitas-hub-stat-value">{metrics.openTasks}</div><p>当前 CRM 任务积压</p></Tile>
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">逾期任务</div><div className="orbitas-hub-stat-value">{metrics.overdueTasks}</div><p>未完成且超过截止日期</p></Tile>
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">客户满意度（CSAT）</div><div className="orbitas-hub-stat-value">—</div><p>需要客服评价数据源</p></Tile>
        </div>
        <div className="orbitas-hub-connector-row"><NotConnected label="客服工单与积压" /><NotConnected label="SLA 达成率与响应时长" /><NotConnected label="一线解决率与坐席负载" /></div>
      </section>

      <section className="orbitas-hub-section">
        <SectionHeading eyebrow="05 / MARKETING & CUSTOMER VALUE" title="营销与客户价值" description="基于现有客户档案查看客户结构；营销归因、客户价值和流失模型需后续接入。" action={<Button kind="tertiary" size="sm" renderIcon={ArrowRight} href="/companies">查看客户 <ArrowRight size={14} /></Button>} />
        <div className="orbitas-hub-grid orbitas-hub-grid--three">
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">客户企业数</div><div className="orbitas-hub-stat-value">{visibleCompanies.length}</div><p>按区域、行业与负责人筛选</p></Tile>
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">联系人数量</div><div className="orbitas-hub-stat-value">{visibleContacts.length}</div><p>已关联筛选条件的联系人</p></Tile>
          <Tile className="orbitas-hub-stat-panel"><div className="orbitas-hub-stat-label">客户行业数</div><div className="orbitas-hub-stat-value">{new Set(visibleCompanies.map((company) => company.sector).filter(Boolean)).size}</div><p>当前客户组合覆盖行业</p></Tile>
        </div>
        <div className="orbitas-hub-connector-row"><NotConnected label="活动效果与渠道 ROI" /><NotConnected label="复购率与客户终身价值" /><NotConnected label="流失预警模型" /></div>
      </section>

      <section className="orbitas-hub-section">
        <SectionHeading eyebrow="06 / COMMERCE" title="电商与商品表现" description="预留为可选业务域；启用前需确认存在独立线上销售渠道。" />
        <Tile className="orbitas-hub-commerce-placeholder"><ShoppingCart size={24} /><div><strong>电商数据源尚未接入</strong><p>订单量、GMV、转化率、客单价及商品表现将在接入电商订单与流量数据后展示。</p></div><Tag type="cool-gray">可选板块</Tag></Tile>
      </section>

      <section className="orbitas-hub-section orbitas-hub-section--last">
        <SectionHeading eyebrow="OPERATIONS / EXCEPTIONS" title="经营风险与待办" description="仅列出 CRM 当前可识别的逾期任务和长期未更新商机。" action={<Button kind="tertiary" size="sm" renderIcon={ArrowRight} href="/tasks">查看全部任务</Button>} />
        <Tile className="orbitas-hub-risk-panel">
          {risks.length ? <div className="orbitas-hub-risk-list">{risks.map((risk) => <Link to={risk.href} key={risk.id} className="orbitas-hub-risk-row"><span className={`orbitas-hub-risk-icon orbitas-hub-risk-icon--${risk.severity}`}>{risk.severity === "high" ? <WarningAlt size={18} /> : <Information size={18} />}</span><span className="orbitas-hub-risk-content"><strong>{risk.title}</strong><span>{risk.detail}</span></span><Tag type={risk.severity === "high" ? "red" : "warm-gray"}>{risk.severity === "high" ? "逾期" : "需跟进"}</Tag><ArrowRight size={16} className="orbitas-hub-risk-arrow" /></Link>)}</div> : <div className="orbitas-hub-empty">当前筛选条件下，没有发现逾期任务或长期未更新的进行中商机。</div>}
          <div className="orbitas-hub-connector-row"><NotConnected label="逾期应收款" /><NotConnected label="交付异常" /><NotConnected label="SLA 超时" /></div>
        </Tile>
      </section>

      <section className="orbitas-hub-section orbitas-hub-activity">
        <DashboardActivityLog />
      </section>

      <footer className="orbitas-hub-footer"><span>Orbitas CRM · 业务数据中枢</span><span>指标口径：CRM 当前可用字段 · 财务及外部系统指标以接入状态标识</span></footer>
    </main>
  );
};
